const SEARCH_RESULT_TYPES = ["course", "topic", "chatbot", "scenario"];

// Identity of a mixed-search result. Child content is keyed by its parent
// composite key plus its own id, so several topics/chatbots/scenarios from one
// course stay distinct while the same item returned twice collapses.
export const searchResultKey = (item) => {
  const type = item?.type || "course";
  const parts = {
    course: [item?.courseId],
    topic: [item?.courseSectionId, item?.topicId],
    chatbot: [item?.courseSectionTopicId, item?.botId],
    scenario: [item?.courseSectionTopicId, item?.roleplayId],
  }[type] || [item?.courseId];
  // An item missing its ids must not share a key with every other such item.
  if (parts.some((part) => !part)) {
    return `${type}:${item?.courseId ?? ""}`;
  }
  return `${type}:${parts.join("|")}`;
};

// The search endpoints echo a next-page cursor on every response, so "is there
// more" is decided here: only continue when some type filled the page limit,
// and never when a page adds no new items (guards against a non-paginating
// source looping forever / a permanent bottom spinner).
export const getNextSearchPageParam = (lastPage, allPages, pageLimit) => {
  const items = lastPage?.Items || [];
  if (items.length === 0) {
    return undefined;
  }
  const seenKeys = new Set(
    allPages
      .slice(0, -1)
      .flatMap((page) => (page?.Items || []).map(searchResultKey)),
  );
  if (!items.some((item) => !seenKeys.has(searchResultKey(item)))) {
    return undefined;
  }
  const limit = Number(pageLimit) || 0;
  const counts = lastPage?.counts || {};
  const aTypeFilledPage =
    limit > 0 &&
    SEARCH_RESULT_TYPES.some((type) => (counts[type] || 0) >= limit);
  return aTypeFilledPage ? lastPage?.LastEvaluatedKey : undefined;
};

// Flatten loaded pages into one list, keeping the first occurrence of each
// result so order is preserved.
export const flattenSearchPages = (pages = []) => {
  const seenKeys = new Set();
  return pages
    .flatMap((page) => page?.Items || [])
    .filter((item) => {
      const key = searchResultKey(item);
      if (seenKeys.has(key)) {
        return false;
      }
      seenKeys.add(key);
      return true;
    });
};
